import cookieConsentService, { type CookieConsentProvider } from "../services/cookieConsent";
import { type ConsentCategories, NO_CONSENT_CATEGORIES } from "../services/cookieConsent/types";
import { reportTelemetryError } from "../telemetry";
import { getDmnAnalyticsIds, isAnalyticsDisabled, normaliseAnalyticsId, resetDmnAnalyticsIds } from "./config";
import { clearGoogleCookies, DENIED_CONSENT_STATE, ownsConsentDecision, toGoogleConsentState } from "./consent";
import { reportConsentRelay } from "./consentAudit";
import { readLocalConsentDecisionTime, recordLocalConsentDecision } from "./consentDecisionRecord";
import { CONSENT_RELAY_PARAM, encodeConsentRelayToken } from "./consentRelay";
import { dispatchAnalyticsDomEvent } from "./domEvent";
import { isSameDocumentEmbed, observeHostDataLayerConsent, resetHostDataLayerConsent } from "./hostDataLayer";
import {
  buildBeginCheckoutPayload,
  buildCartPayload,
  buildFunnelSelectionPayload,
  buildPaymentInitiatedPayload,
  buildPurchasePayload,
  buildWidgetLoadedPayload,
  type PreOrderItemInput,
} from "./events";
import {
  CLIENT_ID_PARAM,
  readCampaignParams,
  SESSION_ID_PARAM,
  validateClientId,
  validateSessionId,
} from "./redirectContinuity";
import {
  configureGa4Property,
  disableConfiguredGa4Properties,
  enableConfiguredGa4Properties,
  getGa4Field,
  isConsentDefaultSent,
  pushConsentDefault,
  pushConsentUpdate,
  resetGa4Properties,
  sendGa4Event,
  sendGa4PageView,
} from "./loaders/gtag";
import { loadGtmContainer, pushToDataLayer, resetGtmContainers } from "./loaders/gtm";
import { buildAnalyticsMessage, postAnalyticsMessage, toDataLayerEvent } from "./postMessage";
import { resolveTrackingSource } from "./source";
import type {
  AnalyticsBooking,
  AnalyticsConfig,
  AnalyticsEcommercePayload,
  AnalyticsEventName,
  AnalyticsFunnelEventName,
  AnalyticsVenue,
  TrackingSource,
} from "./types";

/**
 * sessionStorage key prefix for the once-only guards.
 *
 * A guard key is `collins-analytics-<event>` for an event that may happen once
 * per browser session, and `collins-analytics-<event>:<id>` for one that may
 * happen once per subject — the pending booking for `begin_checkout`, the
 * confirmed booking for `purchase`. Session storage rather than memory is what
 * makes the guard survive a refresh, and per-tab storage is what lets a second
 * tab book again. See `docs/analytics.md`.
 */
const EVENT_GUARD_PREFIX = "collins-analytics-";

/**
 * Name of the total-volume signal raised to DesignMyNight's own destinations for
 * a booking a partner owns.
 *
 * It is deliberately not `purchase`: our GTM container reads the same
 * `window.dataLayer` as a partner's container, so a distinct name is what lets
 * container-side triggers tell our one signal apart from a partner's events. A
 * DesignMyNight-owned booking raises `purchase` to those same destinations
 * instead, so the two names never accompany one another.
 */
export const BOOKING_VOLUME_EVENT = "collins_booking_volume";

/**
 * Purposes assumed when a caller grants consent without naming categories.
 * Advertising is not inferred: it needs an explicit decision.
 */
const INFERRED_CONSENT_CATEGORIES: ConsentCategories = {
  ...NO_CONSENT_CATEGORIES,
  analytics: true,
  performance: true,
};

interface ResolvedDestinations {
  /** DesignMyNight's GA4 property, when configured. */
  readonly dmnMeasurementId: string | null;
  /** DesignMyNight's GTM container, when configured. */
  readonly dmnContainerId: string | null;
  /** The partner's GA4 property, when supplied and the journey is theirs. */
  readonly partnerMeasurementId: string | null;
  /** The partner's GTM container, when supplied and the journey is theirs. */
  readonly partnerContainerId: string | null;
}

/** A GA4 property and GTM container pair belonging to one owner. */
interface DestinationPair {
  readonly measurementId: string | null;
  readonly containerId: string | null;
}

/**
 * The single analytics entry point for Guest Checkout.
 *
 * It owns source classification, consent gating, script injection and event
 * dispatch to Google Analytics 4, Google Tag Manager and the `postMessage`
 * tracking contract. Every public method is guarded: a blocked script,
 * unavailable storage or malformed configuration can never throw into the UI.
 */
export class AnalyticsService {
  private config: AnalyticsConfig = {};
  private trackingSource: TrackingSource = "dmn";
  private consented = false;
  private categories: ConsentCategories | null = null;
  private decisionIsRelayed = false;
  private hostDataLayerConsent: ConsentCategories | null = null;
  private unsubscribeConsent: (() => void) | null = null;
  private unwatchHostDataLayer: (() => void) | null = null;

  constructor(private readonly consentProvider: CookieConsentProvider = cookieConsentService) {}

  /**
   * Supplies the journey's context and starts listening for consent.
   *
   * Nothing is injected here: Google libraries are only loaded once consent is
   * (or becomes) granted.
   */
  configure(config: AnalyticsConfig): void {
    this.guard(() => {
      this.config = config;
      this.trackingSource = resolveTrackingSource({ source: config.source, affiliateId: config.affiliateId });

      this.unsubscribeConsent?.();
      this.unsubscribeConsent = this.consentProvider.onConsentChange((_consented, categories) =>
        this.applyDecision(categories),
      );

      this.watchHostDataLayerConsent();
      this.applyDecision(this.consentProvider.getCategories());

      // A decision relayed from another domain is auditable: what arrived and
      // what the safeguards made of it is recorded server side, once per relay.
      if (config.hostConsentRelay) reportConsentRelay(config.hostConsentRelay);
    });
  }

  /**
   * Grants or withdraws analytics consent.
   *
   * Granting consent injects the relevant libraries immediately, without a page
   * reload; withdrawing it stops all further dispatch, tells Google the decision
   * changed and clears the storage it placed.
   *
   * `categories` carries the decision per purpose. Without it a grant is treated
   * as {@link INFERRED_CONSENT_CATEGORIES}, which keeps advertising refused.
   */
  setConsent(consented: boolean, categories?: ConsentCategories): void {
    this.guard(() => {
      if (categories) {
        this.applyDecision(categories);
        return;
      }

      this.applyDecision(consented ? INFERRED_CONSENT_CATEGORIES : NO_CONSENT_CATEGORIES);
    });
  }

  /** Whether the visitor's decision permits analytics measurement. */
  hasConsent(): boolean {
    return this.consented;
  }

  /** The purposes the visitor granted, or `null` before any decision. */
  getConsentCategories(): ConsentCategories | null {
    return this.categories;
  }

  /** Who commercially owns the current journey. */
  getTrackingSource(): TrackingSource {
    return this.trackingSource;
  }

  /**
   * `widget_loaded` — the widget finished loading.
   *
   * Raised once per browser session: it describes the widget appearing, not a
   * guest action, so a refresh or a return to the form is the same load.
   */
  widgetLoaded(): Promise<void> {
    // A suppressed event has not fired, so it must not be recorded as fired:
    // otherwise a visitor who consents later has it de-duplicated away.
    if (!this.consented || this.hasAlreadyFired("widget_loaded")) return Promise.resolve();
    this.markFired("widget_loaded");

    return this.track("widget_loaded", buildWidgetLoadedPayload(this.venue()));
  }

  /**
   * A funnel selection — the guest chose a venue, booking type, date, party size
   * or time.
   *
   * Each step carries the selection made so far, so the payload shape matches
   * `begin_checkout`. These are not de-duplicated: changing a choice is a new
   * selection and the funnel wants to see it.
   */
  funnelStep(event: AnalyticsFunnelEventName, selection: AnalyticsBooking): Promise<void> {
    return this.track(event, buildFunnelSelectionPayload(selection, this.venue()));
  }

  /**
   * `begin_checkout` — a pending booking exists and the guest reached details.
   *
   * De-duplicated on the pending booking ID in `sessionStorage`, so refreshing
   * the page or resubmitting the form cannot re-fire it for the same pending
   * booking.
   */
  beginCheckout(booking: AnalyticsBooking): Promise<void> {
    if (booking.id) {
      if (!this.consented || this.hasAlreadyFired("begin_checkout", booking.id)) return Promise.resolve();
      this.markFired("begin_checkout", booking.id);
    }

    return this.track("begin_checkout", buildBeginCheckoutPayload(booking, this.venue()));
  }

  /** `add_to_cart` — a pre-order item was added or its quantity increased. */
  addToCart(item: PreOrderItemInput, currency?: string): Promise<void> {
    return this.track("add_to_cart", buildCartPayload(item, this.venue(), currency));
  }

  /** `remove_from_cart` — a pre-order item was removed or its quantity reduced. */
  removeFromCart(item: PreOrderItemInput, currency?: string): Promise<void> {
    return this.track("remove_from_cart", buildCartPayload(item, this.venue(), currency));
  }

  /**
   * `payment_initiated` — client-side or hosted-form validation has passed and
   * finalisation with the provider is about to begin, so the outcome is unknown.
   *
   * Deliberately not de-duplicated: a genuine retry is a new attempt and the
   * funnel wants to see it. Completion is therefore measured by counting
   * distinct `payment_journey_id` values, not raw initiation events.
   */
  paymentInitiated(booking: AnalyticsBooking): Promise<void> {
    return this.track("payment_initiated", buildPaymentInitiatedPayload(booking, this.venue()));
  }

  /**
   * `purchase` — a booking happened.
   *
   * Goes to the destinations owning the journey. A partner-owned booking
   * additionally raises the total-volume signal to DesignMyNight's own
   * destinations, plus the partner's page view and `booking` event; a
   * DesignMyNight-owned booking needs neither, because `purchase` already
   * reached us. Either way exactly one signal of ours is raised per booking.
   *
   * The returned promise settles once the beacons have been dispatched, so
   * callers can hold a redirect open until then.
   *
   * The de-duplication guard lives in `sessionStorage` keyed by booking ID, so
   * refreshing or revisiting the confirmation page cannot re-fire the event.
   */
  async purchase(booking: AnalyticsBooking): Promise<void> {
    if (booking.id && this.hasAlreadyFired("purchase", booking.id)) return;
    if (booking.id) this.markFired("purchase", booking.id);

    const payload = buildPurchasePayload(booking, this.venue());
    await this.track("purchase", payload);

    if (this.trackingSource === "partner") {
      await this.sendBookingVolume(payload);
      await this.sendPartnerConversion(booking, payload);
    }
  }

  /**
   * What the guest's GA4 session and decision look like to a partner we are
   * about to hand them back to.
   *
   * The partner's site is another domain, so their GA4 would otherwise open a
   * new session for a new user and attribute the booking to "direct". Forwarding
   * the identifiers GA4 holds for our own property, the campaign that brought
   * the guest to us, and the decision they took, is what makes the hop
   * continuous.
   *
   * Nothing is produced without a granted decision or without a GA4 property of
   * our own to read: there is then no session to continue and no decision to
   * relay. Reading GA4's identifiers is time-boxed inside {@link getGa4Field},
   * so a blocked or slow tag yields fewer parameters rather than a delayed or
   * broken redirect.
   *
   * @param search query string our own journey arrived on; defaults to the
   * current page's.
   */
  async redirectContinuityParams(search: string = window.location.search): Promise<Record<string, string>> {
    try {
      const { measurementId: dmnMeasurementId } = await getDmnAnalyticsIds();
      const categories = this.categories;
      if (!this.consented || !categories || !dmnMeasurementId) return {};

      const [clientId, sessionId] = await Promise.all([
        getGa4Field(dmnMeasurementId, "client_id"),
        getGa4Field(dmnMeasurementId, "session_id"),
      ]);

      const continuity: Record<string, string> = {
        ...readCampaignParams(search),
        [CONSENT_RELAY_PARAM]: encodeConsentRelayToken(categories, this.decisionTakenAt()),
      };

      const validClientId = validateClientId(clientId);
      const validSessionId = validateSessionId(sessionId);
      if (validClientId) continuity[CLIENT_ID_PARAM] = validClientId;
      if (validSessionId) continuity[SESSION_ID_PARAM] = validSessionId;

      return continuity;
    } catch (error) {
      this.report(error);
      return {};
    }
  }

  /**
   * When the decision the checkout is acting on was taken.
   *
   * A decision relayed to us already carries its own timestamp, which is
   * forwarded unchanged so a chain of hops cannot make an old decision look
   * fresh. Otherwise it is the moment the local decision was first recorded,
   * falling back to now when nothing was recorded — a decision we are acting on
   * but cannot date is treated as current rather than as stale.
   */
  private decisionTakenAt(): number {
    const relay = this.config.hostConsentRelay;
    if (relay?.outcome === "applied" && relay.ageSeconds !== null) {
      return Date.now() - relay.ageSeconds * 1000;
    }

    return readLocalConsentDecisionTime() ?? Date.now();
  }

  /** Resets all state. Test-only. */
  reset(): void {
    this.unsubscribeConsent?.();
    this.unsubscribeConsent = null;
    this.unwatchHostDataLayer?.();
    this.unwatchHostDataLayer = null;
    this.hostDataLayerConsent = null;
    resetHostDataLayerConsent();
    this.config = {};
    this.trackingSource = "dmn";
    this.consented = false;
    this.categories = null;
    this.decisionIsRelayed = false;
    resetDmnAnalyticsIds();
    resetGa4Properties();
    resetGtmContainers();
  }

  /**
   * Applies a consent decision, or the absence of one.
   *
   * Exactly one decision governs a page, and which one depends on whose page it
   * is. Where the checkout owns the decision — on `designmynight.com` — the
   * recorded Termly decision is used and a relayed one is ignored. Embedded on a
   * venue's own domain the checkout never renders a banner, so the host's own
   * decision is the only decision a guest can have made there: without one there
   * is nothing to act on and nothing is emitted, and a decision recorded in the
   * checkout's own context cannot stand in for it. That decision reaches us either
   * from the host's own dataLayer, inline in their document, or relayed on the URL
   * from an iframe embed — see {@link hostDecision}.
   *
   * A withdrawal is reported to Google and the storage it placed is cleared, but
   * only where a library was actually injected — before that there is nothing to
   * withdraw and no cookies of ours to remove.
   */
  private applyDecision(recorded: ConsentCategories | null): void {
    const owns = ownsConsentDecision();
    const categories = owns ? recorded : this.hostDecision();

    // The moment a decision of our own was taken is what a relayed decision has
    // to be newer than to displace it, and nothing else records it.
    if (owns && recorded) recordLocalConsentDecision(recorded);

    this.categories = categories;
    this.decisionIsRelayed = !owns && categories !== null;
    this.consented = categories?.analytics === true && !isAnalyticsDisabled();

    if (!this.consented) {
      disableConfiguredGa4Properties();
      this.withdraw();
      return;
    }

    if (!this.mayLoadGoogle()) return;

    void this.inject(categories as ConsentCategories);
  }

  /**
   * The host page's decision, where the host has stated one.
   *
   * Inline in the host's document, what they pushed to the dataLayer we share is
   * their live decision, so it takes precedence over a value relayed on the URL,
   * which was read from that same dataLayer at some earlier moment.
   */
  private hostDecision(): ConsentCategories | null {
    return this.hostDataLayerConsent ?? this.config.hostConsent ?? null;
  }

  /**
   * Starts reading the host page's decision from the dataLayer we share with it.
   *
   * Only where the host owns the decision, and only inline in their document: in
   * an iframe the dataLayer is the checkout's own and says nothing about the host,
   * so their decision must be relayed to us instead. Watching is idempotent, so
   * re-configuring the service does not install a second observer.
   */
  private watchHostDataLayerConsent(): void {
    if (this.unwatchHostDataLayer) return;
    if (ownsConsentDecision() || !isSameDocumentEmbed()) return;

    this.unwatchHostDataLayer = observeHostDataLayerConsent((categories) => {
      this.hostDataLayerConsent = categories;
      this.applyDecision(this.consentProvider.getCategories());
    });
  }

  /**
   * Whether a Google library may be loaded on the page the checkout is running
   * in: either the checkout owns the consent decision there, or the host site
   * relayed its own decision, which governs its page.
   */
  private mayLoadGoogle(): boolean {
    return ownsConsentDecision() || this.decisionIsRelayed;
  }

  /** Tells Google the decision was withdrawn and removes the storage it placed. */
  private withdraw(): void {
    if (!isConsentDefaultSent()) return;

    pushConsentUpdate(toGoogleConsentState(this.categories ?? NO_CONSENT_CATEGORIES));
    clearGoogleCookies();
  }

  private venue(): AnalyticsVenue | undefined {
    return this.config.venue;
  }

  /**
   * Works out which containers and properties this journey may talk to.
   *
   * Our own IDs are secrets the server holds, so they are asked for here rather
   * than at configuration time: the first ask fetches them and every later one
   * reuses the answer. A DesignMyNight-sourced journey has no partner
   * destination at all. A partner-sourced journey keeps ours resolved, but
   * {@link ownedDestinations} routes measurement to the partner pair and only
   * {@link sendBookingVolume} reaches ours.
   */
  private async resolveDestinations(): Promise<ResolvedDestinations> {
    const dmn = await getDmnAnalyticsIds();
    const isPartner = this.trackingSource === "partner";

    return {
      dmnMeasurementId: dmn.measurementId,
      dmnContainerId: dmn.containerId,
      partnerMeasurementId: isPartner ? normaliseAnalyticsId(this.config.partnerGaAccount) : null,
      partnerContainerId: isPartner ? normaliseAnalyticsId(this.config.partnerGtmAccount) : null,
    };
  }

  /**
   * The property and container owning this journey's measurement.
   *
   * Every event routes here and nowhere else, so a partner's guest is measured
   * into the partner's property and container while a DesignMyNight guest is
   * measured into ours.
   */
  private ownedDestinations(destinations: ResolvedDestinations): DestinationPair {
    const { dmnMeasurementId, dmnContainerId, partnerMeasurementId, partnerContainerId } = destinations;

    if (this.trackingSource === "partner") {
      return { measurementId: partnerMeasurementId, containerId: partnerContainerId };
    }

    return { measurementId: dmnMeasurementId, containerId: dmnContainerId };
  }

  /**
   * Loads the libraries for the destinations this journey may talk to.
   *
   * Safe to call repeatedly: the loaders inject each container and configure
   * each property only once, so re-configuring the service (for example when
   * the venue resolves) picks up new destinations without duplicating scripts.
   */
  private async inject(categories: ConsentCategories): Promise<void> {
    // Google requires the pre-decision state before any measurement command, so a
    // tag that loads early — for example if Termly ever stops blocking it —
    // starts from "denied" rather than assuming consent.
    pushConsentDefault(DENIED_CONSENT_STATE);
    pushConsentUpdate(toGoogleConsentState(categories));

    enableConfiguredGa4Properties();

    const destinations = await this.resolveDestinations();

    // Our own IDs are fetched from the server, so a decision may have been
    // withdrawn while they were in flight: nothing is loaded for it then.
    if (!this.consented) return;

    const { measurementId, containerId } = this.ownedDestinations(destinations);
    const { dmnMeasurementId } = destinations;

    await Promise.all([
      containerId ? loadGtmContainer(containerId) : Promise.resolve(false),
      measurementId ? configureGa4Property(measurementId) : Promise.resolve(false),
      // Our property is configured on every journey so the booking-volume signal
      // has somewhere to land; configuring a property reports nothing by itself.
      dmnMeasurementId ? configureGa4Property(dmnMeasurementId) : Promise.resolve(false),
    ]);
  }

  /**
   * Dispatches one event to the destinations owning the journey.
   *
   * Every dispatch path is consent gated: without a granted decision nothing is
   * posted, pushed or sent, so a refusal — or a visitor who has not decided yet
   * — produces no analytics emission at all.
   *
   * Once consent is granted the `postMessage` envelope is dispatched wherever the
   * checkout runs, including embedded on a venue's own domain where the embedder
   * owns the consent decision and the checkout loads nothing from Google.
   *
   * The same envelope is also dispatched as a DOM `CustomEvent` on the element
   * the checkout is mounted in, which is the mechanism for a same-document
   * embed: an integrator listens with `addEventListener` and needs no Google Tag
   * Manager. It is consent gated, because a host page listening on an ancestor
   * receives it just as an embedder would.
   *
   * Only the owner's container is ever loaded, which is what keeps the dataLayer
   * push — a page-wide broadcast every loaded container reads — confined to the
   * owner's container. That push is derived from the same envelope, so what a
   * container sees and what a host page sees cannot drift apart.
   */
  private async track(event: AnalyticsEventName, payload: AnalyticsEcommercePayload): Promise<void> {
    try {
      if (!this.consented) return;

      const destinations = await this.resolveDestinations();
      const message = buildAnalyticsMessage(event, this.trackingSource, payload);

      postAnalyticsMessage(message);
      dispatchAnalyticsDomEvent(message);

      if (!this.mayLoadGoogle()) return;

      pushToDataLayer(toDataLayerEvent(message));

      const { measurementId } = this.ownedDestinations(destinations);
      if (measurementId) {
        await sendGa4Event(measurementId, event, payload as unknown as Record<string, unknown>);
      }
    } catch (error) {
      this.report(error, event);
    }
  }

  /**
   * Raises the total-volume signal for a partner-owned booking to
   * DesignMyNight's own GA4 property and GTM container.
   *
   * This is the only signal of ours a partner journey produces, so our container
   * is injected here rather than up front, keeping every earlier event to the
   * partner's container alone. The signal carries {@link BOOKING_VOLUME_EVENT} so
   * a container-side trigger can select it on its own.
   */
  private async sendBookingVolume(payload: AnalyticsEcommercePayload): Promise<void> {
    if (!this.consented || !this.mayLoadGoogle()) return;

    const { dmnMeasurementId, dmnContainerId } = await this.resolveDestinations();

    try {
      // A container reads the dataLayer it finds on load, so the signal is queued
      // without waiting for the script: a slow container cannot hold a redirect.
      if (dmnContainerId) void loadGtmContainer(dmnContainerId);
      pushToDataLayer({ event: BOOKING_VOLUME_EVENT, ecommerce: payload });

      if (dmnMeasurementId) {
        await sendGa4Event(dmnMeasurementId, BOOKING_VOLUME_EVENT, payload as unknown as Record<string, unknown>);
      }
    } catch (error) {
      this.report(error, "purchase");
    }
  }

  /**
   * Sends the partner's own conversion signals, mirroring the legacy
   * `partnerTracker`: a page view for the outcome path plus a `booking` event.
   */
  private async sendPartnerConversion(booking: AnalyticsBooking, payload: AnalyticsEcommercePayload): Promise<void> {
    const { partnerMeasurementId } = await this.resolveDestinations();
    if (!this.consented || !this.mayLoadGoogle() || !partnerMeasurementId) return;

    const confirmed = booking.status === "confirmed";
    const pagePath = confirmed ? "/booking-confirmed" : "/enquiry-received";

    try {
      await sendGa4PageView(partnerMeasurementId, pagePath);
      await sendGa4Event(partnerMeasurementId, "booking", {
        ...payload,
        booking_status: confirmed ? "confirmed" : "received",
      });
    } catch (error) {
      this.report(error, "purchase");
    }
  }

  private guardKey(event: AnalyticsEventName, subjectId?: string): string {
    return subjectId ? `${EVENT_GUARD_PREFIX}${event}:${subjectId}` : `${EVENT_GUARD_PREFIX}${event}`;
  }

  private hasAlreadyFired(event: AnalyticsEventName, subjectId?: string): boolean {
    try {
      return window.sessionStorage.getItem(this.guardKey(event, subjectId)) !== null;
    } catch {
      return false;
    }
  }

  private markFired(event: AnalyticsEventName, subjectId?: string): void {
    try {
      window.sessionStorage.setItem(this.guardKey(event, subjectId), "1");
    } catch {
      // Storage unavailable (private browsing, blocked cookies): the event still
      // fires, it simply cannot be de-duplicated across reloads.
    }
  }

  private guard(operation: () => void): void {
    try {
      operation();
    } catch (error) {
      this.report(error);
    }
  }

  private report(error: unknown, event?: AnalyticsEventName): void {
    const normalised = error instanceof Error ? error : new Error(String(error));
    console.warn("[Analytics] Tracking failed:", normalised);
    reportTelemetryError(normalised, event ? { analyticsEvent: event } : undefined);
  }
}

/** The configured analytics instance every component should use. */
const analytics = new AnalyticsService();

export default analytics;
